import { memo, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { getIDFromUrl } from '@rossum/api-client';
import { Queue } from '@rossum/api-client/queues';
import { InlineRenameField } from '@rossum/components/inline-rename/InlineRenameField';
import { useInlineRename } from '@rossum/components/inline-rename/useInlineRename';
import { Tile, TileContextMenuItem } from '@rossum/components/tile';
import {
  IconCopy,
  IconEdit,
  IconFolderSymlink,
  IconTrash,
} from '@rossum/ui/icons/tabler';
import { Box } from '@rossum/ui/material';
import { useRenameQueue } from '../../../../business/queues/useRenameQueue';
import { useGetInbox } from '../../../document-upload/hooks/useGetInbox';
import { useInViewport } from '../../hooks/useInViewport';
import { EngineChipData } from '../../types';
import { SecondaryRow } from './SecondaryRow';
import { useQueueEndChips } from './useQueueEndChips';

export type QueueTileAction = 'move' | 'delete' | 'duplicate';

type QueueTileProps = {
  queue: Queue;
  onAction: (action: QueueTileAction) => void;
  engineChipData: EngineChipData | undefined;
  rulesCount: number;
};

export const QueueTile = memo(
  ({ queue, onAction, engineChipData, rulesCount }: QueueTileProps) => {
    const intl = useIntl();
    const inputRef = useRef<HTMLInputElement>(null);

    const { ref: inViewRef, inView } = useInViewport<HTMLDivElement>({
      // Mount tile content a screenful ahead of scroll so there are no blank
      // frames, and unmount it once scrolled well away to bound the live DOM.
      rootMargin: '600px 0px',
    });

    const { mutate: renameQueue } = useRenameQueue();

    const {
      isRenaming,
      editedName,
      setEditedName,
      handleStartRename,
      handleSaveRename,
      handleCancelRename,
      handleKeyDown,
    } = useInlineRename({
      initialName: queue.name,
      inputRef,
      onSave: newName => renameQueue({ queueId: queue.id, newName }),
    });

    // Render the heavy tile content only near the viewport. Keep it mounted while
    // renaming so an in-progress edit survives the tile scrolling out of view.
    const shouldRenderContent = inView || isRenaming;

    const { data: inbox, isLoading: isInboxLoading } = useGetInbox(
      shouldRenderContent && queue.inbox ? getIDFromUrl(queue.inbox) : 0
    );

    const hasInbox = Boolean(queue.inbox);

    const endChips = useQueueEndChips({
      queue,
      enabled: shouldRenderContent,
      engineChipData,
      rulesCount,
    });

    const contextMenuItems: TileContextMenuItem[] = useMemo(
      () => [
        {
          key: 'rename',
          label: intl.formatMessage({
            id: 'components.workspaces.menu.labels.rename',
          }),
          Icon: IconEdit,
          onClick: handleStartRename,
        },
        {
          key: 'move',
          label: intl.formatMessage({
            id: 'components.workspaces.menu.labels.move',
          }),
          Icon: IconFolderSymlink,
          onClick: () => onAction('move'),
        },
        {
          key: 'duplicate',
          label: intl.formatMessage({
            id: 'components.workspaces.menu.labels.duplicateQueue',
          }),
          Icon: IconCopy,
          onClick: () => onAction('duplicate'),
        },
        {
          key: 'delete',
          label: intl.formatMessage({
            id: 'components.workspaces.menu.labels.delete',
          }),
          Icon: IconTrash,
          onClick: () => onAction('delete'),
        },
      ],
      [intl, handleStartRename, onAction]
    );

    return (
      <Box
        ref={inViewRef}
        sx={{ contentVisibility: 'auto', containIntrinsicSize: '60px' }}
      >
        <Tile
          title={
            shouldRenderContent ? (
              <InlineRenameField
                name={queue.name}
                isRenaming={isRenaming}
                editedName={editedName}
                setEditedName={setEditedName}
                handleKeyDown={handleKeyDown}
                handleSaveRename={handleSaveRename}
                handleCancelRename={handleCancelRename}
                inputRef={inputRef}
                variant="subtitle2"
              />
            ) : null
          }
          description={
            shouldRenderContent ? (
              <SecondaryRow
                id={queue.id}
                email={inbox?.email}
                isEmailLoading={hasInbox && isInboxLoading}
              />
            ) : null
          }
          endChips={shouldRenderContent ? endChips : undefined}
          contextMenuItems={shouldRenderContent ? contextMenuItems : undefined}
          to={`/queues/${queue.id}/settings`}
          dragHandle={null}
          data-cy="queue-tile"
        />
      </Box>
    );
  }
);
